import React, { useState, useEffect } from 'react';
import { SignIn, SignUp } from '@clerk/react';
import { toast } from 'sonner';
import {
  Building2,
  Wand2,
  Loader2,
  ArrowRight,
  CheckCircle2,
  Check,
  X,
  Download,
  Key,
  ChevronRight,
  Layers,
} from 'lucide-react';

// Domain imports
import {
  AppMode,
  ChangeIntent,
  RoomState,
  RoomType,
  StagingStyle,
  STYLE_LABELS,
  ROOM_LABELS,
  INTENTS_BY_ROOM_STATE,
  INTENT_LABELS,
  INTENT_DESCRIPTIONS,
  ROOM_STATE_LABELS,
  ROOM_STATE_DESCRIPTIONS,
  intentToAppMode,
  ImageAsset,
  ProcessingState,
  VariantState,
} from './types';
import { generateVariant, saveRender } from './services/pipelineService';
import { apiFetch } from './services/api';
import { useRoomDetection } from './hooks/useRoomDetection';
import { trackEvent, updateUserProperties } from './services/analytics';
import { generationErrorMessage, renderSaveErrorMessage } from './services/renderError';
import { FileUpload } from './components/FileUpload';
import { StagingStepper } from './components/StagingStepper';
import { ComparisonSlider } from './components/ComparisonSlider';
import { VariantSelector } from './components/VariantSelector';
import { ProcessingSidebar } from './components/ProcessingSidebar';
import { StudioControls } from './components/StudioControls';
import { OnboardingTooltip } from './components/OnboardingTooltip';
import { PropertyManager, Property } from './components/PropertyManager';
import { TeamDashboard } from './components/TeamDashboard';
import { ProfilePage } from './components/ProfilePage';
import { AppNav } from './components/AppNav';
import { AppTopBar } from './components/AppTopBar';
import { OverviewPage } from './components/OverviewPage';
import { SubscriptionPage } from './components/SubscriptionPage';
import { ReferralsPage } from './components/ReferralsPage';
import { FeedbackPage } from './components/FeedbackPage';
import { CommandPalette } from './components/CommandPalette';
import { AdminConsole } from './components/AdminConsole';
import { ScopedErrorBoundary } from './components/ScopedErrorBoundary';
import { useAuth } from './hooks/useAuth';
import { clerkSignInUrl, clerkSignUpUrl, clerkAfterSignInUrl, clerkAfterSignUpUrl } from './src/auth/ClerkProvider';
import { getReferralCode } from './src/auth/referralCode';
import TaskSetupMFA from './src/components/TaskSetupMFA';
import { useAppView, useUrlPropertyId } from './src/routes/useAppView';
import { isFullyCovered, roomCoveragePct } from './src/rooms/coverage';


const App: React.FC = () => {
  // --- Auth (extracted to hook) ---
  const { user, setUser, hasKey, setHasKey, showOnboarding, setShowOnboarding, isLoadingProfile, profileLoadFailed, authLoaded, userLoaded, orgLoaded, orgId, clerkOrgId, claims, handleLogout, handleConnectKey } = useAuth();

  // --- Application State ---
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  // STA-198: when set, PropertyManager auto-opens its detail view + SmartUpload
  // (batch flow). Set door Start staging CTA op overzicht; door PropertyManager
  // gewist via onAutoStartConsumed nadat de panel open is.
  const [autoStartProperty, setAutoStartProperty] = useState<Property | null>(null);

  // STAGERY-71: View state is URL-driven via React Router. `useAppView` is the
  // single bridge that keeps the existing `setView(x)`-everywhere API while
  // making the URL the source of truth.
  const [view, setView] = useAppView('overview');
  // STA-163: the render wizard always carries `:propertyId` in the URL, but the
  // `selectedProperty` state below is lost on reload / direct navigation. This
  // read-only URL value is the persistence-safe fallback for the property id.
  const urlPropertyId = useUrlPropertyId();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [assignedUploadRoom, setAssignedUploadRoom] = useState<RoomType | null>(null);
  
  const [images, setImages] = useState<ImageAsset>({
    original: null,
    renovated: null,
    staged: null
  });
  
  const [config, setConfig] = useState({
    // BUG-134 — two-step picker (Sam, 2026-09-06): ask whether the room is
    // empty or furnished FIRST, then offer only the 2-3 intents that make
    // sense for that answer. A makelaar never sees more than 3 buttons even
    // though four real intents exist.
    //
    // `mode` stays in sync via intentToAppMode() because it still pins the
    // pipeline (renovate pass yes/no). The INTENT is what the render is
    // judged against by the API's budget gates.
    roomState: RoomState.FURNISHED,
    changeIntent: ChangeIntent.SWAP,
    mode: AppMode.STAGING_ONLY,
    roomType: RoomType.LIVING_ROOM,
    style: StagingStyle.MODERN,
    variantCount: 1,
    // BUG-129c — free-form instruction. Presets cannot say "vervang de bank"
    // or "eiken keukenfronten, donkere wand"; this can. Wrapped server-side by
    // the same structural guardrails as every other prompt.
    userPrompt: '',
  });

  const roomDetection = useRoomDetection(selectedFile, roomType => {
    setConfig(prev => ({ ...prev, roomType }));
  }, assignedUploadRoom);

  const [processing, setProcessing] = useState<ProcessingState>({
    isProcessing: false,
    step: 'idle',
    progress: 0,
    message: ''
  });

  // Studio UI State
  const [showEmptyBase, setShowEmptyBase] = useState(false);

  // Command palette (⌘K) state
  const [cmdkOpen, setCmdkOpen] = useState(false);

  // Sidebar collapse state (persisted)
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('stagery_sidebar_collapsed') === '1';
  });
  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('stagery_sidebar_collapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  // History + Pipeline state
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [regenerateFeedback, setRegenerateFeedback] = useState('');
  const [historyVersion, setHistoryVersion] = useState(0);
  const [variants, setVariants] = useState<VariantState[]>([]);
  const [isSavingVariant, setIsSavingVariant] = useState(false);
  const [nextRoomSuggestion, setNextRoomSuggestion] = useState<string | null>(null);

  // Batch restyle state (Start staging → parallel rendering)
  const [batchJobs, setBatchJobs] = useState<{ roomType: RoomType; file: File; status: 'pending' | 'processing' | 'done' | 'error'; result?: { staged: string; original: string | null }; error?: string }[]>([]);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);

  // STA-137: show banner 3s after load (prevent flash), auto-dismiss after 15s.
  // P1-03: also suppress immediately when Clerk's native orgId is set — the
  // kantoor_id JWT claim can lag the Clerk webhook by several seconds, causing
  // the banner to persist on mobile even after org creation completed.
  const [showOrgBanner, setShowOrgBanner] = useState(false);
  useEffect(() => {
    if (!orgLoaded || orgId || clerkOrgId) { setShowOrgBanner(false); return; }
    const show = setTimeout(() => setShowOrgBanner(true), 3_000);
    const hide = setTimeout(() => setShowOrgBanner(false), 15_000);
    return () => { clearTimeout(show); clearTimeout(hide); };
  }, [orgLoaded, orgId, clerkOrgId]);

  // --- App Handlers ---

  // ⌘K / Ctrl+K global hotkey
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCmdkOpen(o => !o);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const handleSelectProperty = (property: Property, preselectedRoom?: string, file?: File, style?: StagingStyle) => {
    if (style) setConfig(prev => ({ ...prev, style }));
    setSelectedProperty(property);
    if (preselectedRoom) {
      setConfig(prev => ({ ...prev, roomType: preselectedRoom as RoomType }));
    }
    if (file) {
      handleFileSelect(file, preselectedRoom as RoomType | undefined);
      setView('dashboard', property.id);
    } else {
      setView('dashboard', property.id);
      resetImages();
    }
  };

  // STA-198 follow-up: Start staging CTA op overzicht moet naar de batch
  // upload-flow in PropertyManager (meerdere foto's tegelijk), niet naar de
  // single-photo Studio. Zet autoStartProperty en switch view → PropertyManager
  // pakt 'm op via useEffect en opent direct het SmartUpload-panel.
  const handleStartStagingMulti = (property: Property) => {
    setAutoStartProperty(property);
    setView('properties', property.id);
  };

  const handleBatchRestyle = async (property: Property, photos: { file: File; roomType: RoomType }[], style: StagingStyle = config.style) => {
    setConfig(prev => ({ ...prev, style }));
    setSelectedProperty(property);
    const jobs = photos.map(p => ({ roomType: p.roomType, file: p.file, status: 'pending' as const }));
    setBatchJobs(jobs);
    setIsBatchProcessing(true);
    setView('batch', property.id);

    trackEvent('batch_restyle_started', { roomCount: photos.length });

    // Process all rooms in parallel
    const promises = jobs.map(async (job, idx) => {
      setBatchJobs(prev => prev.map((j, i) => i === idx ? { ...j, status: 'processing' } : j));
      try {
        const result = await generateVariant({
          file: job.file,
          existingRenovated: null,
          mode: config.mode,
          changeIntent: config.changeIntent,
          roomType: job.roomType,
          style,
          userPrompt: config.userPrompt,
          // STA-185: pipeline persists the render row with property_id
          // immediately on completion, so even if the user navigates away
          // mid-batch the render still lands in the woning.
          propertyId: property.id,
          onProgress: () => {},
        });
        setBatchJobs(prev => prev.map((j, i) => i === idx ? { ...j, status: 'done', result: { staged: result.staged, original: result.original } } : j));

        // Save render to backend
        if (user) {
          const saveRes = await saveRender({
            propertyId: property.id,
            stagedImage: result.staged,
            originalImage: result.original,
            roomType: job.roomType,
            mode: config.mode,
            generationType: 'initial',
            feedbackText: null,
            jobId: result.jobId,
          });
          if (saveRes.credits !== undefined) {
            setUser(prev => prev ? { ...prev, credits: saveRes.credits } : prev);
          }
        }
      } catch (err: any) {
        setBatchJobs(prev => prev.map((j, i) => i === idx ? { ...j, status: 'error', error: err.message } : j));
      }
    });

    await Promise.allSettled(promises);
    setIsBatchProcessing(false);
    setHistoryVersion(v => v + 1);
    trackEvent('batch_restyle_completed', { roomCount: photos.length });
  };

  const handleFileSelect = (file: File, assignedRoom?: RoomType) => {
    setAssignedUploadRoom(assignedRoom ?? null);
    setSelectedFile(file);
    const objectUrl = URL.createObjectURL(file);
    setImages({ original: objectUrl, renovated: null, staged: null });
    trackEvent('file_selected', { fileType: file.type, fileSize: file.size });
  };

  /**
   * BUG-131 - `iterateOnCurrent` makes the next pass build ON the render that
   * is on screen instead of restarting from the uploaded photo. That is what
   * "doorwerken op dit resultaat" means: keep the light oak and the green
   * wall, now also add a fireplace.
   *
   * Guard: only a real https URL from our blob store can be fed back. A blob:
   * or data: preview URL is local to the browser and the API could never
   * fetch it, so we fall back to a normal run rather than fail the request.
   */
  const executePipeline = async (
    isRestyle = false,
    generationType: 'initial' | 'restyle' | 'correction' = 'initial',
    iterateOnCurrent = false,
  ) => {
    const iterateUrl =
      iterateOnCurrent && typeof images.staged === 'string' && images.staged.startsWith('https://')
        ? images.staged
        : null;
    if (!selectedFile && !images.renovated && !iterateUrl) return;

    trackEvent(isRestyle ? 'restyle_started' : 'generation_started', {
      mode: config.mode,
      roomType: config.roomType
    });

    setElapsedSeconds(0);
    let timerRef: ReturnType<typeof setInterval>;
    timerRef = setInterval(() => setElapsedSeconds(s => s + 1), 1000);

    setProcessing({ isProcessing: true, step: 'uploading', progress: 5, message: 'Analyzing Structure' });

    // STA-185: resolve the property up-front so the pipeline can persist the
    // render row with property_id immediately on completion (no extra click /
    // navigation race).
    const earlyPropertyId: number | null =
      selectedProperty?.id ?? (urlPropertyId != null ? Number(urlPropertyId) : null);

    try {
      // All AI work is dispatched to stagery-api via /api/ai/stage. The server
      // handles renovation + staging inside Inngest; the client only polls for
      // progress and gets back the final URLs.
      const result = await generateVariant({
        file: selectedFile,
        existingRenovated: images.renovated,
        baseRenderUrl: iterateUrl,
        mode: config.mode,
        changeIntent: config.changeIntent,
        roomType: config.roomType,
        feedback: regenerateFeedback || undefined,
        isRestyle,
        style: config.style,
        userPrompt: config.userPrompt,
        propertyId: earlyPropertyId != null && Number.isFinite(earlyPropertyId) ? earlyPropertyId : null,
        onProgress: (step, progress) => {
          const mappedStep: ProcessingState['step'] = step === 'done' ? 'complete' : step === 'uploading' ? 'uploading' : 'staging';
          setProcessing({ isProcessing: true, step: mappedStep, progress, message: step === 'uploading' ? 'Uploaden' : step === 'staging' ? 'Inrichten' : 'Klaar' });
        },
      });

      const originalDataUrl = result.original;
      if (result.renovated) {
        setImages((prev) => ({ ...prev, renovated: result.renovated }));
      }
      const stagedUrl = result.staged;
      setImages((prev) => ({ ...prev, staged: stagedUrl }));
      clearInterval(timerRef);
      setProcessing({ isProcessing: false, step: 'complete', progress: 100, message: 'Complete' });
      setView('studio');

      // STA-185: the Inngest pipeline already persisted the render row with
      // property_id (we passed `propertyId` to /api/ai/stage above). This
      // follow-up POST now serves three purposes only: trigger the
      // referral-reward activation on first ever render, refresh the
      // user.credits balance after the pipeline's consume-credit step, and
      // refresh local history. The backend dedupes on `jobId` so this never
      // inserts a duplicate row.
      // STA-163: resolve the property from the URL when `selectedProperty`
      // state was dropped (reload / direct navigation) so the render persists.
      const propertyId: number | null = earlyPropertyId;
      if (propertyId != null && Number.isFinite(propertyId) && user) {
        apiFetch(`/api/properties/${propertyId}/renders`, {
          method: 'POST',
          body: {
            stagedImage: stagedUrl,
            originalImage: originalDataUrl,
            roomType: config.roomType,
            mode: config.mode,
            generationType,
            feedbackText: regenerateFeedback.trim() || null,
            jobId: result.jobId,
          },
        })
        .then(async (data: any) => {
          if (data?.credits !== undefined) {
            setUser(prev => prev ? { ...prev, credits: data.credits } : prev);
          }
          setHistoryVersion(v => v + 1);
        })
        .catch((err: any) => {
          toast.error(renderSaveErrorMessage(err));
        });
      }

      setRegenerateFeedback(''); // feedback wissen na succesvolle generatie

      trackEvent('generation_completed', {
         isRestyle,
         hasRenovation: config.mode === AppMode.FULL_RENOVATION
      });

    } catch (error: any) {
      clearInterval(timerRef);
      console.error(error);
      setProcessing({ isProcessing: false, step: 'idle', progress: 0, message: 'Error' });
      trackEvent('generation_failed', { error: error.message });

      if (error.message?.includes('Requested entity was not found')) {
         toast.error('API Key invalid or expired. Please select a key again.');
         setHasKey(false);
      } else {
         toast.error(generationErrorMessage(error));
      }
    }
  };

  const handleGenerate = () => {
    const count = config.variantCount ?? 1;
    if (user && user.credits < count) {
      toast.error(`Niet genoeg credits. ${count} variant${count !== 1 ? 'en' : ''} kost ${count} credit${count !== 1 ? 's' : ''}.`);
      return;
    }
    executeMultiVariant();
  };

  const executeMultiVariant = async () => {
    if (!selectedFile) return;

    const count = Math.max(1, Math.min(3, config.variantCount ?? 1));
    trackEvent('multi_variant_started', { mode: config.mode, roomType: config.roomType, count });

    const ids = Array.from({ length: count }, (_, i) => i);
    const initial: VariantState[] = ids.map(id => ({
      id, original: null, renovated: null, staged: null,
      status: 'pending', progress: 5, error: null,
    }));
    setVariants(initial);
    setView('variants');

    // STA-185: propertyId is intentionally NOT passed in the multi-variant
    // flow. The user picks one of N variants; only the picked one should be
    // attached to the woning (handleSelectVariant POSTs with jobId, which
    // patches the chosen pipeline row with property_id and leaves the
    // unchosen rows orphaned-by-design).
    const multiPropertyId: number | null = null;

    const promises = ids.map(id =>
      generateVariant({
        file: selectedFile,
        existingRenovated: null,
        mode: config.mode,
        changeIntent: config.changeIntent,
        roomType: config.roomType,
        style: config.style,
        userPrompt: config.userPrompt,
        propertyId: multiPropertyId,
        onProgress: (step, progress) => {
          const variantStatus: VariantState['status'] = step === 'done' ? 'done' : step === 'uploading' ? 'pending' : 'staging';
          setVariants(prev => prev.map(v =>
            v.id === id ? { ...v, status: variantStatus, progress } : v
          ));
        },
      })
        .then(result => {
          setVariants(prev => prev.map(v =>
            v.id === id ? { ...v, ...result, status: 'done', progress: 100 } : v
          ));
        })
        .catch(err => {
          setVariants(prev => prev.map(v =>
            v.id === id ? { ...v, status: 'error', error: err.message } : v
          ));
        })
    );

    await Promise.allSettled(promises);
    trackEvent('multi_variant_completed', { mode: config.mode, roomType: config.roomType, count });
  };

  const handleSelectVariant = async (variantId: number) => {
    const variant = variants.find(v => v.id === variantId);
    if (!variant || !variant.staged) return;

    setImages({
      original: variant.original,
      renovated: variant.renovated,
      staged: variant.staged,
    });

    // STA-163: `selectedProperty` state is lost on a page reload or direct
    // navigation, but the render-wizard URL always carries `:propertyId`. Fall
    // back to the URL so the render is still persisted — otherwise saveRender
    // is silently skipped and the completed render is never saved.
    const propertyId: number | null =
      selectedProperty?.id ?? (urlPropertyId != null ? Number(urlPropertyId) : null);

    if (propertyId != null && Number.isFinite(propertyId)) {
      setIsSavingVariant(true);
      try {
        // STA-185: jobId comes from the pipeline run; backend dedupes against
        // staging_jobs.render_id so this never inserts a duplicate row.
        const result = await saveRender({
          propertyId,
          stagedImage: variant.staged,
          originalImage: variant.original,
          roomType: config.roomType,
          mode: config.mode,
          generationType: 'initial',
          feedbackText: null,
          jobId: variant.jobId ?? null,
        });
        setUser(prev => prev ? { ...prev, credits: result.credits } : prev);
        setHistoryVersion(v => v + 1);

        // PLG tracking: render completion events
        trackEvent('room_completed', { roomType: config.roomType, propertyId });

        // Check first render ever (user had default credits, now has less)
        if (user && user.credits === result.credits + 1) {
          apiFetch<Array<{ room_type: string }>>(`/api/properties/${propertyId}/renders`)
            .then(renders => {
              const totalUserRenders = renders.length;
              if (totalUserRenders === 1) {
                trackEvent('first_render_completed', { roomType: config.roomType });
              }
              const coveredRooms = new Set(renders.map((r) => r.room_type));
              if (isFullyCovered(coveredRooms)) {
                trackEvent('property_fully_covered', { propertyId, totalRenders: renders.length });
              }
              updateUserProperties({ total_renders: totalUserRenders, room_coverage_pct: roomCoveragePct(coveredRooms) });
            })
            .catch(() => {});
        }
        if (result.credits <= 0) {
          trackEvent('credits_exhausted');
        }
      } catch (err: any) {
        toast.error(renderSaveErrorMessage(err));
      } finally {
        setIsSavingVariant(false);
      }
    }

    setVariants([]);
    setView('studio');
    trackEvent('variant_selected', { variantId });

    // Bepaal volgende ontbrekende ruimte als suggestie
    if (propertyId != null && Number.isFinite(propertyId) && user) {
      apiFetch<Array<{ room_type: string }>>(`/api/properties/${propertyId}/renders`)
        .then(renders => {
          const covered = new Set(renders.map((r) => r.room_type));
          const allRooms = [RoomType.LIVING_ROOM, RoomType.BEDROOM, RoomType.KITCHEN, RoomType.BATHROOM];
          const next = allRooms.find(r => !covered.has(r));
          setNextRoomSuggestion(next || null);
        })
        .catch(() => setNextRoomSuggestion(null));
    }
  };

  const handleRestyle = () => {
    executePipeline(true, 'restyle');
  };

  const handleRegenerate = () => {
    // Volledige reset + opnieuw genereren vanaf origineel
    setImages(prev => ({ ...prev, renovated: null, staged: null }));
    executePipeline(false, regenerateFeedback.trim() ? 'correction' : 'initial');
  };

  /**
   * BUG-131 - doorwerken op het resultaat dat nu op het scherm staat.
   *
   * Anders dan "Opnieuw restylen" (dat gooit alles weg en begint opnieuw bij
   * de originele foto) neemt dit de huidige render ALS input, zodat je erop
   * kunt stapelen: "hou dit, zet er nu ook een open haard in".
   *
   * Bewust STAGING_ONLY: FULL_RENOVATION laat de pipeline de ruimte eerst
   * leegruimen, en op een al ingerichte render betekent dat je meubels
   * kwijtraakt. Live gemeten 2026-09-05 op job stg_0ab810ef: de hoekbank
   * verdween uit de vervolgrender.
   */
  const handleIterate = () => {
    setConfig(prev => ({ ...prev, mode: AppMode.STAGING_ONLY }));
    executePipeline(false, 'correction', true);
  };

  const handleDownload = () => {
    const currentImage = showEmptyBase && images.renovated ? images.renovated : images.staged;
    if (!currentImage) return;

    trackEvent('download_image', { type: showEmptyBase ? 'renovated_base' : 'staged_final' });

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);

      // Label: from localStorage or default
      const label = localStorage.getItem('stagery_export_label') || 'Digitale herinrichting';
      const fontSize = Math.max(12, Math.round(img.width * 0.018));
      ctx.font = `bold ${fontSize}px monospace`;
      const padding = Math.round(fontSize * 0.6);
      const textWidth = ctx.measureText(label).width;
      const boxW = textWidth + padding * 2;
      const boxH = fontSize + padding * 2;
      const x = img.width - boxW - padding;
      const y = img.height - boxH - padding;

      // Semi-transparante witte achtergrond
      ctx.fillStyle = 'rgba(255,255,255,0.82)';
      ctx.fillRect(x, y, boxW, boxH);

      // Tekst
      ctx.fillStyle = '#1a1a1a';
      ctx.fillText(label, x + padding, y + padding + fontSize * 0.85);

      canvas.toBlob(blob => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Stagery_${showEmptyBase ? 'Renovated' : 'Staged'}_${Date.now()}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/jpeg', 0.92);
    };
    img.src = currentImage;
  };

  const resetImages = () => {
    setSelectedFile(null);
    setImages({ original: null, renovated: null, staged: null });
    setProcessing({ isProcessing: false, step: 'idle', progress: 0, message: '' });
    setVariants([]);
    setIsSavingVariant(false);
  };

  const resetApp = () => {
    trackEvent('reset_project');
    setView('properties');
    setSelectedProperty(null);
    resetImages();
  };

  const onLogout = () => {
    handleLogout();
    setSelectedProperty(null);
    setView('properties');
  };

  // --- Clerk drop-in auth routes ---
  // Renders Clerk's branded <SignIn /> / <SignUp /> at explicit URLs.
  // Exact path or sub-paths only (e.g. /sign-up/verify-email-address) — not
  // other paths that merely share a string prefix like /sign-upload.
  const authPath = typeof window !== 'undefined' ? window.location.pathname : '/';

  // --- Admin console route — fallback for when VITE_USE_REACT_ROUTER is off ---
  // When React Router is enabled, BeheerconsoleRoute handles /beheerconsole with
  // its own gate. When the flag is off (default in prod), App.tsx is the router
  // and must handle both /beheerconsole and the legacy /admin path.
  // IMPORTANT: check isLoadingProfile first so we never flash the dashboard
  // while the profile fetch is still in-flight.
  if (authPath === '/beheerconsole' || authPath === '/admin' || authPath.startsWith('/admin/')) {
    if (!authLoaded || !userLoaded || isLoadingProfile) {
      return (
        <div
          className="min-h-screen bg-bg flex items-center justify-center"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <Loader2 size={24} className="animate-spin text-ink/40" aria-hidden="true" />
          <span className="sr-only">Laden…</span>
        </div>
      );
    }
    if (!user) {
      window.location.replace(clerkSignInUrl + '?redirect_url=' + encodeURIComponent('/beheerconsole'));
      return null;
    }
    // D-07: claims.app_role is the canonical gate; fallback to user.role on first render.
    const adminRole = claims?.app_role ?? user.role;
    if (adminRole !== 'superadmin') {
      window.location.replace('/');
      return null;
    }
    return (
      <AdminConsole user={user} />
    );
  }
  if (authPath === clerkSignUpUrl || authPath.startsWith(clerkSignUpUrl + '/')) {
    const refCode = getReferralCode();
    return (
      <div className="min-h-screen w-screen bg-bg text-ink flex items-center justify-center p-6">
        <SignUp
          routing="path"
          path={clerkSignUpUrl}
          signInUrl={clerkSignInUrl}
          fallbackRedirectUrl={clerkAfterSignUpUrl}
          signInFallbackRedirectUrl={clerkAfterSignInUrl}
          unsafeMetadata={refCode ? { referralCode: refCode } : undefined}
        />
      </div>
    );
  }
  // STG-1 (formerly DUTCH-20): Device Trust is handled on the React Router `/sign-in/*` route
  // (`src/routes/pages/SignIn.tsx` + `ClientTrustFallback`). This App.tsx branch is
  // leftover for the pre-router path and is not the production sign-in surface.
  if (authPath === clerkSignInUrl || authPath.startsWith(clerkSignInUrl + '/')) {
    return (
      <div className="min-h-screen w-screen bg-bg text-ink flex items-center justify-center p-6">
        <SignIn
          routing="path"
          path={clerkSignInUrl}
          signUpUrl={clerkSignUpUrl}
          fallbackRedirectUrl={clerkAfterSignInUrl}
          signUpFallbackRedirectUrl={clerkAfterSignUpUrl}
        />
      </div>
    );
  }

  // --- No Key State ---
  if (!hasKey) {
    return (
      <div className="h-screen w-screen bg-bg text-ink flex flex-col items-center justify-center p-8">
         <div className="max-w-md w-full border border-border p-8 bg-white shadow-card rounded-lg text-center">
            <div className="w-16 h-16 bg-black text-white flex items-center justify-center mx-auto mb-6 rounded-md">
               <span className="font-sans font-bold text-3xl pt-1">S</span>
            </div>
            <h1 className="font-sans text-3xl font-bold mb-4">Stagery</h1>
            <p className="text-sm text-ink/70 mb-8 leading-relaxed">
              To use the <strong>Gemini 3 Pro</strong> imaging models for professional real estate staging, please connect a paid Google Cloud Project API key.
            </p>
            <button 
              onClick={handleConnectKey}
              className="w-full bg-ink text-white py-4 font-bold uppercase tracking-wide hover:bg-accent hover:text-ink transition-colors flex items-center justify-center gap-2 rounded-md"
            >
              <Key size={18} />
              Connect API Key
            </button>
            <a 
              href="https://ai.google.dev/gemini-api/docs/billing" 
              target="_blank" 
              rel="noreferrer"
              className="block mt-6 text-[10px] uppercase tracking-wide text-ink/40 hover:text-ink underline underline-offset-4"
            >
              View Billing Documentation
            </a>
         </div>
      </div>
    );
  }

  // --- Auth loading — Clerk initialising or profile fetch in-flight ---
  // Prevents the flash of LoginScreen / sign-up form that users see after
  // completing Clerk sign-in/sign-up and landing back on /.
  if (!authLoaded || !userLoaded || isLoadingProfile) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-ink/40" />
      </div>
    );
  }

  // Clerk session OK but /api/auth/me failed — show retry/sign-out UI
  // instead of bouncing to /sign-in (Clerk would just complete sign-in
  // again, creating an infinite redirect loop). JIT provisioning in
  // stagery-api now covers the "no DB row" case, so this branch only
  // fires on real backend errors (5xx, network, expired key).
  if (profileLoadFailed) {
    return (
      <div className="min-h-screen bg-bg text-ink flex items-center justify-center p-6">
        <div className="max-w-md w-full border border-border p-8 bg-white shadow-card rounded-lg text-center">
          <h1 className="font-sans text-2xl font-bold mb-3">Even geduld…</h1>
          <p className="text-sm text-ink/70 mb-6 leading-relaxed">
            We konden je profiel niet laden. Probeer het opnieuw of meld je af en log opnieuw in.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="bg-ink text-white px-5 py-2 font-bold uppercase tracking-wide hover:bg-accent hover:text-ink transition-colors rounded-md"
            >
              Opnieuw proberen
            </button>
            <button
              type="button"
              onClick={() => handleLogout()}
              className="border border-border px-5 py-2 font-bold uppercase tracking-wide hover:bg-bg/50 transition-colors rounded-md"
            >
              Uitloggen
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Unauthenticated state — bounce to Clerk's /sign-in for any non-public path.
  // The custom LoginScreen was removed: Clerk's <SignIn /> / <SignUp /> drop-ins
  // (rendered above for /sign-in and /sign-up) are the single auth surface.
  if (!user) {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      const PUBLIC_PREFIXES = [clerkSignInUrl, clerkSignUpUrl];
      const isPublic = PUBLIC_PREFIXES.some(p => path === p || path.startsWith(p + '/'));
      if (!isPublic) {
        // Preserve the full URL (pathname + query string + hash) so referral
        // links and deep links survive the auth round-trip.
        const fullPath = path + window.location.search + window.location.hash;
        const redirectQs = fullPath === '/' ? '' : '?redirect_url=' + encodeURIComponent(fullPath);
        window.location.replace(clerkSignInUrl + redirectQs);
        return null;
      }
    }
    // SSR / non-browser fallback (or already on a public Clerk route while
    // unauthenticated — Clerk's drop-in renders itself).
    return null;
  }

  // --- MFA enforcement for superadmins (D-05) ---
  // Rendered unconditionally here; TaskSetupMFA returns null for non-superadmins
  // and for superadmins who already have MFA enabled.

  // D-07: claims.app_role is the canonical gate. Falls back to user.role when claims
  // not yet resolved (first render before template fetch completes).
  const appRole = (claims?.app_role as 'beheerder' | 'medewerker' | 'superadmin' | undefined) ?? user.role;

  // --- Views with shared navigation ---
  // STA-199: `startStaging` shares the PropertyManager surface met `properties`
  // (alleen verschil: autoStartStagingForId vanuit URL). Dus zelfde nav-shell.
  const navView = view === 'overview' || view === 'profile' || view === 'team' || view === 'properties' || view === 'startStaging' || view === 'subscription' || view === 'referrals' || view === 'feedback';
  // Nav-componenten hebben hun eigen smallere AppView type dat geen
  // `startStaging` kent — voor highlight-doeleinden is dat hetzelfde als
  // `properties`. Cast hier zodat we de child types niet hoeven aan te raken.
  const navView_view = (view === 'startStaging' ? 'properties' : view) as Parameters<typeof AppNav>[0]['view'];
  if (navView) {
    return (
      <div className="min-h-screen bg-bg text-ink flex">
        <TaskSetupMFA />
        {showOnboarding && (
          <OnboardingTooltip
            onDone={() => setShowOnboarding(false)}
            credits={user.credits}
            firstName={user.firstName}
          />
        )}
        <AppNav
          view={navView_view}
          onNavigate={(v) => setView(v)}
          appRole={appRole}
          collapsed={sidebarCollapsed}
          onToggleCollapse={toggleSidebar}
          onLogout={onLogout}
        />
        <CommandPalette
          open={cmdkOpen}
          onClose={() => setCmdkOpen(false)}
          onNavigate={(v) => setView(v)}
          onSelectProperty={(p) => handleSelectProperty(p)}
          onLogout={onLogout}
          userRole={appRole}
        />
        {/* AppNav's mobile menu is the single phone navigation surface. */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Phase 1: kantoor provisioning banner — 3s delay to prevent flash, auto-hides after 15s */}
          {showOrgBanner && (
            <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-sm text-amber-800 text-center">
              Uw kantoor wordt aangemaakt&hellip; Dit duurt slechts enkele seconden.
            </div>
          )}
          <AppTopBar
            user={user}
            onNavigate={(v) => setView(v)}
            onLogout={onLogout}
            onOpenSearch={() => setCmdkOpen(true)}
          />
          <main className="flex-1 min-w-0">
          {view === 'overview' && (
            <ScopedErrorBoundary scope="OverviewPage">
              <OverviewPage
                user={user}
                onNavigateProperties={() => setView('properties')}
                onNavigateSubscription={() => setView('subscription')}
                onNavigate={(v) => setView(v)}
                onSelectProperty={(p) => handleSelectProperty(p)}
                onStartStaging={handleStartStagingMulti}
                onCreditsUpdate={(credits) => setUser(prev => prev ? { ...prev, credits } : prev)}
              />
            </ScopedErrorBoundary>
          )}
          {view === 'profile' && (
            <ScopedErrorBoundary scope="ProfilePage">
              <ProfilePage user={user} onBack={() => setView('overview')} onLogout={onLogout} onUpdate={(updated) => setUser(prev => prev ? { ...prev, ...updated } : prev)} />
            </ScopedErrorBoundary>
          )}
          {view === 'referrals' && (
            <ScopedErrorBoundary scope="ReferralsPage">
              <ReferralsPage user={user} />
            </ScopedErrorBoundary>
          )}
          {view === 'feedback' && (
            <ScopedErrorBoundary scope="FeedbackPage">
              <FeedbackPage />
            </ScopedErrorBoundary>
          )}
          {view === 'team' && (appRole === 'beheerder' || appRole === 'superadmin') && (
            <ScopedErrorBoundary scope="TeamDashboard">
              <TeamDashboard user={user} />
            </ScopedErrorBoundary>
          )}
          {view === 'subscription' && (
            <ScopedErrorBoundary scope="SubscriptionPage">
              <SubscriptionPage user={user} />
            </ScopedErrorBoundary>
          )}
          {(view === 'properties' || view === 'startStaging') && (
            <ScopedErrorBoundary scope="PropertyManager">
              <PropertyManager
                user={user}
                onSelect={handleSelectProperty}
                onBatchRestyle={handleBatchRestyle}
                onCreditsUpdate={(credits) => setUser(prev => prev ? { ...prev, credits } : prev)}
                autoStartStagingFor={autoStartProperty}
                autoStartStagingForId={view === 'startStaging' && urlPropertyId ? Number(urlPropertyId) : null}
                onAutoStartConsumed={() => setAutoStartProperty(null)}
              />
            </ScopedErrorBoundary>
          )}
          </main>
        </div>
      </div>
    );
  }

  // --- Main App Interface ---
  const isLoading = processing.isProcessing;

  const handleHistoryRestore = (stagedUrl: string) => {
    setImages({ original: null, renovated: null, staged: stagedUrl });
    setView('studio');
  };

  return (
    <div className="h-screen w-screen bg-bg text-ink font-sans overflow-hidden flex">
      <TaskSetupMFA />
      {showOnboarding && user && (
        <OnboardingTooltip
          onDone={() => setShowOnboarding(false)}
          credits={user.credits}
          firstName={user.firstName}
        />
      )}

      <AppNav
        view={view}
        onNavigate={(v) => setView(v)}
        appRole={appRole}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebar}
        onLogout={onLogout}
      />
      <CommandPalette
        open={cmdkOpen}
        onClose={() => setCmdkOpen(false)}
        onNavigate={(v) => setView(v)}
        onSelectProperty={(p) => handleSelectProperty(p)}
        onLogout={onLogout}
        userRole={appRole}
      />

      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
      <AppTopBar
        user={user}
        onNavigate={(v) => setView(v)}
        onLogout={onLogout}
        onOpenSearch={() => setCmdkOpen(true)}
      />
      {/* 1. Studio Header (breadcrumb + stepper) */}
      <header className="h-12 border-b border-black/8 flex items-center justify-between px-6 bg-white shrink-0">
        <div className="flex items-center gap-3">
          {selectedProperty && (
            <div className="hidden md:flex items-center gap-2">
              <button onClick={() => { setView('properties'); }} className="text-[11px] text-ink/55 hover:text-accent uppercase tracking-[0.08em] font-semibold transition-colors">
                ← Woningen
              </button>
              <span className="text-ink/25">·</span>
              <span className="text-[11px] font-semibold text-ink/70 truncate max-w-40">{selectedProperty.address}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4">
            <StagingStepper view={view} hasPhoto={Boolean(selectedFile)} />
        </div>
      </header>

      {/* 2. Main Content Area */}
      {/* Mobile: Vertical Flex (Canvas Top, Controls Bottom) */}
      {/* Desktop: Horizontal Flex (Canvas Left, Sidebar Right) */}
      <main className="flex-grow flex flex-col lg:flex-row overflow-hidden relative">
        
        {/* PANEL A: Canvas / Viewfinder */}
        {/* Mobile: 40% height. Desktop: Flexible width, full height */}
        <div className="w-full h-[40vh] lg:h-full lg:flex-1 bg-stone-100 relative flex flex-col overflow-hidden shrink-0 border-b lg:border-b-0 lg:border-r border-border">
          
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" 
               style={{ 
                 backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', 
                 backgroundSize: '20px 20px' 
               }}>
          </div>

          <div className="flex-grow flex items-center justify-center p-4 md:p-8 relative h-full">
            {/* VIEW: DASHBOARD - UPLOAD OR PREVIEW */}
            {view === 'dashboard' && (
              <div className="w-full h-full max-w-5xl flex items-center justify-center">
                 {!images.original ? (
                    <FileUpload onFileSelect={handleFileSelect} className="h-full w-full md:max-h-[600px] border-2 shadow-sm bg-white" />
                 ) : (
                    <div className="relative w-full h-full flex items-center justify-center group">
                       <div className="relative max-w-full max-h-full shadow-lg md:shadow-2xl border-2 md:border-4 border-white">
                          <img
                            src={images.original}
                            alt="Origineel"
                            className="max-w-full max-h-full md:max-h-[80vh] object-contain"
                          />
                          {/* Remove Button */}
                          {!isLoading && (
                            <button 
                              onClick={() => {
                                setImages({ ...images, original: null });
                                setSelectedFile(null);
                              }}
                              className="absolute -top-3 -right-3 bg-black text-white p-2 rounded-full shadow-lg hover:bg-red-600 transition-colors z-20"
                            >
                              <X size={16} />
                            </button>
                          )}
                          <div className="absolute bottom-3 left-3 md:bottom-4 md:left-4 bg-black/80 backdrop-blur text-white px-3 py-1 md:px-3.5 md:py-1.5 text-xs font-semibold tracking-[0.04em] rounded-full">
                            Origineel
                          </div>
                       </div>
                    </div>
                 )}
              </div>
            )}

            {/* VIEW: VARIANTS - 3-column selection */}
            {view === 'variants' && (
              <ScopedErrorBoundary scope="VariantSelector">
                <VariantSelector
                  variants={variants}
                  onSelect={handleSelectVariant}
                  isSaving={isSavingVariant}
                />
              </ScopedErrorBoundary>
            )}

            {/* VIEW: BATCH - Parallel room rendering */}
            {view === 'batch' && (
              <div className="w-full h-full flex flex-col items-center justify-center gap-6 p-8 animate-in fade-in duration-300">
                <div className="text-center mb-4">
                  <h2 className="text-xl font-semibold mb-1">Batch restyling</h2>
                  <p className="text-sm text-ink/60">
                    {batchJobs.filter(j => j.status === 'done').length}/{batchJobs.length} kamers klaar
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-3xl">
                  {batchJobs.map((job, idx) => {

                    return (
                      <div
                        key={idx}
                        className={`border rounded-lg overflow-hidden transition-all ${job.status === 'done' ? 'border-accent shadow-card cursor-pointer hover:shadow-card-hover' : job.status === 'error' ? 'border-red-300' : 'border-border'}`}
                        onClick={() => {
                          if (job.status === 'done' && job.result) {
                            setImages({ original: job.result.original, renovated: null, staged: job.result.staged });
                            setConfig(prev => ({ ...prev, roomType: job.roomType }));
                            setAssignedUploadRoom(job.roomType);
                            setSelectedFile(job.file);
                            setView('studio');
                          }
                        }}
                      >
                        <div className="aspect-[4/3] bg-stone relative">
                          {job.status === 'done' && job.result ? (
                            <>
                              <img src={job.result.staged} alt={`Gestylede ${ROOM_LABELS[job.roomType] || job.roomType} kamer`} className="w-full h-full object-cover" />
                              <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                                <span className="bg-white/90 text-ink text-xs font-medium px-3 py-1.5 rounded-md shadow">Bekijk &amp; corrigeer</span>
                              </div>
                            </>
                          ) : job.status === 'error' ? (
                            <div className="w-full h-full flex items-center justify-center text-red-500 text-xs">{job.error || 'Fout'}</div>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              {job.status === 'processing' ? (
                                <Loader2 size={24} className="animate-spin text-ink/40" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-border" />
                              )}
                            </div>
                          )}
                        </div>
                        <div className="p-3 flex items-center justify-between bg-white">
                          <span className="text-sm font-medium">{ROOM_LABELS[job.roomType] || job.roomType}</span>
                          <span className={`text-[10px] font-bold uppercase tracking-wide ${job.status === 'done' ? 'text-green-600' : job.status === 'error' ? 'text-red-500' : job.status === 'processing' ? 'text-ink/50' : 'text-ink/30'}`}>
                            {job.status === 'done' ? 'Klaar ↗' : job.status === 'error' ? 'Fout' : job.status === 'processing' ? 'Bezig...' : 'Wachten'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {!isBatchProcessing && batchJobs.some(j => j.status === 'done') && (
                  <button
                    onClick={() => { setView('properties'); setBatchJobs([]); }}
                    className="bg-ink text-white px-6 py-3 rounded-md text-sm font-medium hover:opacity-85 transition-opacity flex items-center gap-2"
                  >
                    <Check size={16} /> Terug naar woning
                  </button>
                )}
              </div>
            )}

            {/* VIEW: STUDIO - COMPARISON */}
            {view === 'studio' && images.original && images.staged && (
              <div className="w-full h-full flex items-center justify-center animate-in fade-in zoom-in-95 duration-500">
                 <div className="relative w-full h-full shadow-2xl border border-stone-300 bg-white">
                    <ComparisonSlider 
                      beforeImage={images.original} 
                      afterImage={showEmptyBase && images.renovated ? images.renovated : images.staged}
                      labelBefore="SOURCE"
                      labelAfter={showEmptyBase ? "BASE" : "STAGED"}
                    />
                 </div>
              </div>
            )}
          </div>
        </div>

        {/* PANEL B: Sidebar Controls */}
        {/* Mobile: Remaining height (flex-1), scrollable. Desktop: Fixed width (w-96), full height. */}
        <div className="w-full flex-1 lg:w-96 lg:flex-none bg-bg flex flex-col shrink-0 z-20 shadow-[-10px_0_30px_-15px_rgba(0,0,0,0.1)] overflow-hidden">
          
          {/* Sidebar Header */}
          <div className="h-14 md:h-16 border-b border-black/8 flex items-center px-6 shrink-0 bg-bg">
            <h2 className="font-sans text-base md:text-lg font-bold tracking-[-0.01em] flex items-center gap-2.5">
              {isLoading ? (
                <>
                   <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-accent text-white flex items-center justify-center text-xs font-bold"><Loader2 size={12} className="animate-spin"/></div>
                   Bezig…
                </>
              ) : view === 'dashboard' ? (
                <>
                  <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-accent text-white flex items-center justify-center text-xs font-bold">2</div>
                  Instellingen
                </>
              ) : view === 'variants' ? (
                <>
                  <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-accent text-white flex items-center justify-center text-xs font-bold">3</div>
                  Kies versie
                </>
              ) : view === 'batch' ? (
                <>
                  <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-accent text-white flex items-center justify-center text-xs font-bold">{isBatchProcessing ? <Loader2 size={12} className="animate-spin" /> : '✓'}</div>
                  Batch restyling
                </>
              ) : (
                <>
                  <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-accent text-white flex items-center justify-center text-xs font-bold">4</div>
                  Resultaat
                </>
              )}
            </h2>
          </div>

          {/* Sidebar Content Scrollable */}
          <div className="order-2 flex-grow overflow-y-auto p-4 md:p-6 flex flex-col gap-6 md:gap-8 bg-bg">
            
            {/* STATE: PROCESSING */}
            {isLoading ? (
               <ScopedErrorBoundary scope="ProcessingSidebar">
                 <ProcessingSidebar processing={processing} mode={config.mode} elapsedSeconds={elapsedSeconds} />
               </ScopedErrorBoundary>
            ) : (
              <>
                {/* MODE: DASHBOARD CONTROLS */}
                {view === 'dashboard' && (
                  <>
                    {/* BUG-134 — tweestapskeuze (Sam, 2026-09-06). Eerst de
                        vraag of de ruimte leeg of gemeubileerd is, daarna
                        alleen de 2-3 opties die daarbij horen. Een makelaar
                        ziet zo nooit meer dan 3 knoppen, terwijl er vier
                        werkelijke intenties bestaan.

                        BUG-129e blijft van kracht: geen `hidden md:block`,
                        want juist op mobiel telt de keuze het meest. */}
                    <div className="space-y-3">
                        <label className="text-[11px] font-bold text-ink/55 uppercase tracking-[0.08em] flex items-center gap-2">
                          <Layers size={13} />
                          Staat van de ruimte
                        </label>
                        <div className="grid grid-cols-2 gap-2.5">
                          {(Object.values(RoomState) as RoomState[]).map((rs) => (
                            <button
                              key={rs}
                              onClick={() => {
                                // Switching room state can invalidate the picked
                                // intent (Opknappen makes no sense for an empty
                                // room), so snap to the first valid option.
                                const allowed = INTENTS_BY_ROOM_STATE[rs];
                                const nextIntent = allowed.includes(config.changeIntent)
                                  ? config.changeIntent
                                  : allowed[0];
                                setConfig({
                                  ...config,
                                  roomState: rs,
                                  changeIntent: nextIntent,
                                  mode: intentToAppMode(nextIntent),
                                });
                              }}
                              className={`text-left p-3.5 border rounded-xl transition-all duration-200 ${config.roomState === rs ? 'bg-white border-accent/40 shadow-[0_4px_14px_-6px_rgba(241,110,0,0.25)]' : 'bg-white/60 border-black/10 hover:border-black/20 hover:bg-white'}`}
                            >
                              <div className="flex justify-between items-start gap-1.5">
                                <span className="font-bold text-sm text-ink tracking-[-0.01em]">{ROOM_STATE_LABELS[rs]}</span>
                                {config.roomState === rs && <CheckCircle2 size={15} className="text-accent shrink-0" />}
                              </div>
                              <p className="text-[11px] text-ink/60 mt-1 leading-snug">
                                {ROOM_STATE_DESCRIPTIONS[rs]}
                              </p>
                            </button>
                          ))}
                        </div>
                    </div>

                    <div className="space-y-3">
                        <label className="text-[11px] font-bold text-ink/55 uppercase tracking-[0.08em] flex items-center gap-2">
                          <Layers size={13} />
                          Wat moet er veranderen
                        </label>
                        <div className="space-y-2.5">
                          {INTENTS_BY_ROOM_STATE[config.roomState].map((intent) => (
                            <button
                              key={intent}
                              onClick={() => setConfig({
                                ...config,
                                changeIntent: intent,
                                mode: intentToAppMode(intent),
                              })}
                              className={`w-full text-left p-4 border rounded-xl transition-all duration-200 ${config.changeIntent === intent ? 'bg-white border-accent/40 shadow-[0_4px_14px_-6px_rgba(241,110,0,0.25)]' : 'bg-white/60 border-black/10 hover:border-black/20 hover:bg-white'}`}
                            >
                              <div className="flex justify-between items-start gap-2">
                                <span className="font-bold text-sm text-ink tracking-[-0.01em]">{INTENT_LABELS[intent]}</span>
                                {config.changeIntent === intent && <CheckCircle2 size={16} className="text-accent shrink-0" />}
                              </div>
                              <p className="text-xs text-ink/60 mt-1.5 leading-relaxed">
                                {INTENT_DESCRIPTIONS[intent]}
                              </p>
                            </button>
                          ))}
                        </div>
                    </div>

                    {/* Room Selection */}
                    <div className="space-y-3">
                        <label className="text-[11px] font-bold text-ink/55 uppercase tracking-[0.08em] flex items-center gap-2">
                          <Building2 size={13} />
                          Ruimtetype
                        </label>
                        <div className="relative">
                          <select
                            value={config.roomType}
                            onChange={(e) => roomDetection.override(e.target.value as RoomType)}
                            className="w-full bg-white border border-black/10 hover:border-black/20 p-3 md:p-3.5 pr-10 text-sm font-medium focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 appearance-none cursor-pointer rounded-xl transition-all shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                          >
                            {Object.values(RoomType).map(type => (
                              <option key={type} value={type}>{ROOM_LABELS[type] ?? type}</option>
                            ))}
                          </select>
                          <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                            <ChevronRight size={16} className="text-ink/60 rotate-90" />
                          </div>
                        </div>
                        {selectedFile && (
                          <p role="status" className="text-xs text-muted">
                            {roomDetection.status === 'detecting' ? 'Ruimte herkennen… Je kunt zelf kiezen.'
                              : roomDetection.status === 'detected' ? `Herkend: ${ROOM_LABELS[config.roomType] ?? config.roomType}. Je kunt dit wijzigen.`
                              : roomDetection.status === 'failed' ? 'Herkenning niet beschikbaar. Kies het ruimtetype handmatig.'
                              : roomDetection.status === 'manual' ? 'Handmatig gekozen ruimtetype.' : null}
                          </p>
                        )}
                        {/* Stijlkeuze */}
                        <div className="mt-4 space-y-2.5">
                          <label className="text-[11px] font-bold text-ink/55 uppercase tracking-[0.08em] flex items-center gap-2">
                            Stijl
                          </label>
                          <div className="relative">
                            <select
                              value={config.style}
                              onChange={(e) => setConfig({ ...config, style: e.target.value as StagingStyle })}
                              className="w-full bg-white border border-black/10 hover:border-black/20 p-3 md:p-3.5 pr-10 text-sm font-medium focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 appearance-none cursor-pointer rounded-xl transition-all shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                            >
                              {Object.values(StagingStyle).map(s => (
                                <option key={s} value={s}>{STYLE_LABELS[s]}</option>
                              ))}
                            </select>
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                              <ChevronRight size={16} className="text-ink/60 rotate-90" />
                            </div>
                          </div>
                        </div>

                        {/* Aantal varianten */}
                        <div className="mt-4 space-y-2.5">
                          <label className="text-[11px] font-bold text-ink/55 uppercase tracking-[0.08em] flex items-center justify-between gap-2">
                            <span>Aantal varianten</span>
                            <span className="text-ink/40 font-medium normal-case tracking-normal text-[11px]">{config.variantCount} credit{config.variantCount !== 1 ? 's' : ''}</span>
                          </label>
                          <div className="grid grid-cols-3 gap-2">
                            {[1, 2, 3].map(n => (
                              <button
                                key={n}
                                type="button"
                                onClick={() => setConfig({ ...config, variantCount: n })}
                                className={`py-3 text-sm font-bold border rounded-xl transition-all ${
                                  config.variantCount === n
                                    ? 'bg-accent text-white border-accent shadow-[0_6px_18px_-8px_rgba(241,110,0,0.5)]'
                                    : 'bg-white text-ink border-black/10 hover:border-accent/40 hover:bg-accent/5'
                                }`}
                              >
                                {n}×
                              </button>
                            ))}
                          </div>
                          <p className="text-xs text-ink/55 mt-1 leading-relaxed">
                            {config.variantCount === 1
                              ? '1 render · ~60 sec'
                              : config.variantCount === 2
                                ? '2 versies parallel · ~2 min · kies de beste'
                                : '3 versies parallel · ~2-3 min · kies de beste'}
                          </p>
                        </div>

                        {/* BUG-129c — eigen wensen, vrij tekstveld */}
                        <div className="mt-4 space-y-2.5">
                          <label
                            htmlFor="user-prompt"
                            className="text-[11px] font-bold text-ink/55 uppercase tracking-[0.08em] flex items-center justify-between gap-2"
                          >
                            <span>Eigen wensen</span>
                            <span className="text-ink/40 font-medium normal-case tracking-normal text-[11px]">
                              optioneel
                            </span>
                          </label>
                          <textarea
                            id="user-prompt"
                            value={config.userPrompt}
                            onChange={(e) => setConfig({ ...config, userPrompt: e.target.value.slice(0, 500) })}
                            rows={3}
                            maxLength={500}
                            placeholder="Bijv. vervang de bank door een lichte hoekbank, eiken keukenfronten, donkergroene wandkleur"
                            className="w-full bg-white border border-black/10 hover:border-black/20 p-3 md:p-3.5 text-sm font-medium focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 rounded-xl transition-all shadow-[0_1px_2px_rgba(0,0,0,0.04)] resize-none placeholder:text-ink/35"
                          />
                          <p className="text-xs text-ink/55 leading-relaxed">
                            Schrijf in gewone taal wat je anders wilt. Ramen, muren en het camerastandpunt blijven altijd zoals op de foto.
                          </p>
                        </div>
                    </div>
                  </>
                )}

                {/* MODE: VARIANTS CONTROLS */}
                {view === 'variants' && (
                  <>
                    <div className="p-4 bg-white border border-black/6 rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                      <h3 className="font-sans text-base md:text-lg font-bold text-ink tracking-[-0.01em] mb-1">
                        {variants.length === 1 ? 'Eén render' : `${variants.length} varianten`}
                      </h3>
                      <p className="text-xs font-medium text-ink/55">
                        {ROOM_LABELS[config.roomType] ?? config.roomType} · {config.mode === AppMode.FULL_RENOVATION ? 'Volledig restylen' : 'Meubels toevoegen'}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[11px] font-bold text-ink/55 uppercase tracking-[0.08em]">
                        Voortgang
                      </label>
                      {variants.map(v => (
                        <div key={v.id} className="flex items-center gap-2.5 px-3 py-2.5 border border-black/8 bg-white rounded-xl">
                          <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                            v.status === 'done' ? 'bg-accent' :
                            v.status === 'error' ? 'bg-red-500' :
                            'bg-ink/30 animate-pulse'
                          }`} />
                          <span className="text-sm font-semibold text-ink">Variant {v.id + 1}</span>
                          <span className="text-xs font-medium text-ink/55 ml-auto">
                            {v.status === 'done' ? 'Klaar' :
                             v.status === 'error' ? 'Fout' :
                             `${v.progress}%`}
                          </span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-ink/55 leading-relaxed text-center">
                      Klik op "Kies deze variant" bij je favoriete resultaat. Alleen de gekozen versie kost 1 credit.
                    </p>
                  </>
                )}

                {/* MODE: BATCH PROGRESS */}
                {view === 'batch' && (
                  <div className="space-y-4">
                    <p className="text-xs text-ink/60">Alle geselecteerde kamers worden tegelijk gerestyled. Dit duurt 1-2 minuten.</p>
                    {batchJobs.map((job, idx) => {

                      return (
                        <div key={idx} className="flex items-center gap-3 p-3 border border-border rounded-md bg-white">
                          {job.status === 'done' ? (
                            <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center"><Check size={12} /></div>
                          ) : job.status === 'processing' ? (
                            <div className="w-6 h-6 rounded-full bg-ink flex items-center justify-center"><Loader2 size={12} className="text-white animate-spin" /></div>
                          ) : job.status === 'error' ? (
                            <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-red-500 text-xs">!</div>
                          ) : (
                            <div className="w-6 h-6 rounded-full border border-border" />
                          )}
                          <div className="flex-1">
                            <span className="text-sm font-medium">{ROOM_LABELS[job.roomType] || job.roomType}</span>
                          </div>
                          <span className={`text-[10px] font-bold uppercase tracking-wide ${job.status === 'done' ? 'text-green-600' : job.status === 'error' ? 'text-red-500' : job.status === 'processing' ? 'text-ink/60' : 'text-ink/30'}`}>
                            {job.status === 'done' ? 'Klaar' : job.status === 'error' ? 'Fout' : job.status === 'processing' ? 'Bezig...' : 'Wachten'}
                          </span>
                        </div>
                      );
                    })}
                    {!isBatchProcessing && (
                      <div className="pt-2">
                        <p className="text-xs text-ink/50 mb-3">
                          {batchJobs.filter(j => j.status === 'done').length} van {batchJobs.length} kamers klaar.
                          {batchJobs.some(j => j.status === 'error') && ` ${batchJobs.filter(j => j.status === 'error').length} mislukt.`}
                        </p>
                        <button
                          onClick={() => { setView('properties'); setBatchJobs([]); }}
                          className="w-full bg-ink text-white py-3 rounded-md text-sm font-medium hover:opacity-85 transition-opacity flex items-center justify-center gap-2"
                        >
                          Terug naar woning
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* MODE: STUDIO CONTROLS */}
                {view === 'studio' && (
                  <ScopedErrorBoundary scope="StudioControls">
                    <StudioControls
                      config={config}
                      images={images}
                      showEmptyBase={showEmptyBase}
                      setShowEmptyBase={setShowEmptyBase}
                      regenerateFeedback={regenerateFeedback}
                      setRegenerateFeedback={setRegenerateFeedback}
                      isLoading={isLoading}
                      onRegenerate={handleRegenerate}
                      onRestyle={handleRestyle}
                      onIterate={handleIterate}
                      canIterate={typeof images.staged === 'string' && images.staged.startsWith('https://')}
                      propertyId={selectedProperty?.id ?? null}
                      historyVersion={historyVersion}
                      onHistoryRestore={handleHistoryRestore}
                    />
                  </ScopedErrorBoundary>
                )}
              </>
            )}

          </div>

          {/* Sidebar Footer (Primary Action) */}
          {/* STA-186: action precedes scrollable settings, so it stays visible
              on mobile without relying on clearance under the bottom navigation. */}
          <div className="order-1 p-4 md:p-6 border-t border-border bg-white shrink-0">
             {/* If Loading, hide primary actions to focus on progress */}
             {isLoading ? (
               <div className="flex items-center justify-center gap-2 text-xs uppercase text-ink/40">
                 <Loader2 size={12} className="animate-spin" /> Processing Request
               </div>
             ) : (
               <>
                {view === 'dashboard' ? (
                    <button
                      onClick={handleGenerate}
                      disabled={!selectedFile || isLoading}
                      className="staging-primary-action"
                    >
                      <Wand2 size={18} />
                      <span>Start restyling</span>
                    </button>
                ) : view === 'variants' ? (
                  <div className="flex items-center justify-center gap-2 text-xs uppercase text-ink/40">
                    <Loader2 size={12} className={variants.every(v => v.status === 'done' || v.status === 'error') ? 'hidden' : 'animate-spin'} />
                    {variants.every(v => v.status === 'done' || v.status === 'error')
                      ? 'Kies een versie hierboven'
                      : 'Restylings worden gemaakt…'}
                  </div>
                ) : view === 'batch' ? (
                  <div className="space-y-3">
                    <button
                      onClick={() => { setView('properties'); setBatchJobs([]); }}
                      className="staging-primary-action"
                    >
                      <Check size={18} />
                      Terug naar woning
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <button
                        onClick={handleDownload}
                        className="staging-primary-action"
                      >
                        <Download size={18} />
                        Download
                      </button>
                      {/* STAGERY-59: keep the same input photo but pick a different style */}
                      {!batchJobs.length && images.original && (
                        <button
                          onClick={() => {
                            trackEvent('try_another_style_clicked', { previousStyle: config.style });
                            setImages(prev => ({ ...prev, renovated: null, staged: null }));
                            setView('dashboard');
                          }}
                          className="w-full bg-white text-ink border border-border py-3 px-6 flex items-center justify-center gap-2 font-bold text-xs uppercase tracking-wide hover:bg-stone-100 transition-colors"
                        >
                          <Layers size={14} />
                          Probeer een andere stijl
                        </button>
                      )}
                      {batchJobs.length > 0 && (
                        <button
                          onClick={() => setView('batch')}
                          className="w-full bg-white text-ink border border-border py-3 px-6 flex items-center justify-center gap-2 font-bold text-xs uppercase tracking-wide hover:bg-stone-100 transition-colors rounded-md"
                        >
                          ← Batch overzicht ({batchJobs.filter(j => j.status === 'done').length}/{batchJobs.length})
                        </button>
                      )}
                      {!batchJobs.length && nextRoomSuggestion && selectedProperty && (
                        <button
                          onClick={() => {
                            setConfig(prev => ({ ...prev, roomType: nextRoomSuggestion as RoomType }));
                            setView('dashboard');
                            resetImages();
                            setNextRoomSuggestion(null);
                          }}
                          className="w-full bg-white text-ink border border-border py-3 px-6 flex items-center justify-center gap-2 font-bold text-xs uppercase tracking-wide hover:bg-stone-100 transition-colors"
                        >
                          Volgende: {nextRoomSuggestion}
                        </button>
                      )}
                      <button
                        onClick={() => { if (batchJobs.length) { setView('properties'); setBatchJobs([]); } else { resetApp(); } }}
                        className="w-full py-2 text-xs font-bold uppercase tracking-wide text-ink/40 hover:text-ink hover:underline underline-offset-4"
                      >
                        Terug naar woningen
                      </button>
                  </div>
                )}
               </>
             )}
          </div>

        </div>
      </main>
      </div>
    </div>
  );
};

export default App;